'use client';

import { useState } from 'react';

import { Star, StarHalf } from 'lucide-react';

import { cn } from '@/shared/utils/utils';

interface RateProps {
  /** Текущее значение (0..count). Для readOnly можно дробное при allowHalf. */
  value?: number;
  /** Количество звёзд */
  count?: number;
  /** Только чтение — без интерактива */
  readOnly?: boolean;
  /** Разрешить дробные значения при отображении (полузвёзды) */
  allowHalf?: boolean;
  /** Колбэк при выборе (только в интерактивном режиме) */
  onChange?: (_value: number) => void;
  className?: string;
  /** Размер иконки в px (аналог style.fontSize в antd-mobile Rate) */
  size?: number;
  style?: React.CSSProperties;
}

/**
 * Замена antd-mobile `Rate`. Поддерживает readOnly + allowHalf (для отображения)
 * и интерактивный выбор целых значений (как использовалось в форме отзыва).
 */
export const Rate = ({
  value = 0,
  count = 5,
  readOnly = false,
  allowHalf = false,
  onChange,
  className,
  size,
  style,
}: RateProps) => {
  const [hover, setHover] = useState<number | null>(null);

  const active = hover ?? value;
  const iconSize = size ?? (style?.fontSize ? parseFloat(String(style.fontSize)) : 20);

  return (
    <div
      className={cn('inline-flex items-center gap-0.5', className)}
      style={style}
      role={readOnly ? 'img' : 'radiogroup'}
      aria-label={`Оценка ${value} из ${count}`}
    >
      {Array.from({ length: count }, (_, i) => {
        const starValue = i + 1;
        const filled = active >= starValue;
        const half = !filled && allowHalf && active >= starValue - 0.5;

        const Icon = half ? StarHalf : Star;

        return (
          <button
            key={i}
            type="button"
            disabled={readOnly}
            tabIndex={readOnly ? -1 : 0}
            aria-label={`${starValue}`}
            className={cn(
              'shrink-0 leading-none',
              !readOnly && 'cursor-pointer',
            )}
            onClick={readOnly ? undefined : () => onChange?.(starValue)}
            onMouseEnter={readOnly ? undefined : () => setHover(starValue)}
            onMouseLeave={readOnly ? undefined : () => setHover(null)}
          >
            <Icon
              size={iconSize}
              className={cn(
                filled || half ? 'text-yellow-400' : 'text-gray-300',
              )}
              fill={filled || half ? 'currentColor' : 'none'}
              strokeWidth={1.5}
            />
          </button>
        );
      })}
    </div>
  );
};
